/**
 * versus AI — admin management of the assistant.
 *
 * Tile A · versus soul    — view / edit the soul (system prompt). Saving here is
 *                           the single source of truth for what the AI uses;
 *                           changes take effect on the next session, no app release.
 * Tile B · AI user notes  — look up a user and view/edit the short keyword notes
 *                           the AI keeps about them. These are DEDICATED AI notes,
 *                           separate from any other admin/KYC notes on the user.
 *
 * Design: reuses the panel's existing card/header/button/alert components
 * (VouchersPage.css) so it matches the rest of the dashboard exactly.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './VouchersPage.css';
import './VersusAIPage.css';

interface SoulHistoryRow {
  id: string;
  systemPrompt: string;
  savedAt: string;
  savedByAdminId: string | null;
}

const VersusAIPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  return (
    <div className="vouchers-page versus-ai-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{'AI Soul & Notes'}</h1>
          <div className="page-sub">The assistant's system prompt + the per-user notes it keeps</div>
        </div>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError(null)}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess(null)}>{success}</div>}

      <SoulTile onError={setError} onSuccess={setSuccess} />
      <UserNotesTile onError={setError} onSuccess={setSuccess} />
    </div>
  );
};

// ───────────────────────────── Tile A · versus soul ─────────────────────────

const SoulTile: React.FC<{ onError: (m: string) => void; onSuccess: (m: string) => void }> = ({
  onError,
  onSuccess,
}) => {
  const [soul, setSoul] = useState<string>('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [history, setHistory] = useState<SoulHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([apiService.getAISoul(), apiService.getAISoulHistory(20)]);
      setSoul(s.systemPrompt ?? '');
      setUpdatedAt(s.updatedAt ?? null);
      setHistory(h.history ?? []);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load the soul');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  const startEdit = (seed?: string) => {
    setDraft(seed ?? soul);
    setEditing(true);
  };

  const save = async () => {
    if (!draft.trim()) { onError('The soul cannot be empty.'); return; }
    setSaving(true);
    try {
      await apiService.updateAISoul(draft);
      setEditing(false);
      onSuccess('Soul saved. The AI uses it on the next session — no app release needed.');
      await load();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to save the soul');
    } finally {
      setSaving(false);
    }
  };

  // Version label: each save snapshots the previous soul into history, so the
  // current live version number is history.length + 1.
  const versionLabel = `v${history.length + 1}`;

  return (
    <div className="card soul-card">
      <div className="card-body">
        <div className="soul-head">
          <div>
            <h2 className="section-h">versus soul</h2>
            <div className="soul-meta">
              <span className="soul-version">{versionLabel}</span>
              {updatedAt && <span> · last edited {new Date(updatedAt).toLocaleString()}</span>}
              {history.length > 0 && (
                <span> · <button className="linkish" onClick={() => setShowHistory((v) => !v)}>
                  {showHistory ? 'hide' : 'view'} history ({history.length})
                </button></span>
              )}
            </div>
          </div>
          {!editing && !loading && (
            <button className="btn btn-primary" onClick={() => startEdit()}>Edit</button>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading the soul…</div>
        ) : editing ? (
          <>
            <textarea
              className="soul-editor"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
            />
            <div className="soul-actions">
              <button className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !draft.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <div className="soul-view">{soul}</div>
        )}

        {showHistory && history.length > 0 && (
          <div className="soul-history">
            <h3 className="soul-history-h">Version history</h3>
            {history.map((h) => (
              <div key={h.id} className="soul-history-row">
                <span className="soul-history-when">{new Date(h.savedAt).toLocaleString()}</span>
                <span className="soul-history-preview">{h.systemPrompt.slice(0, 80).replace(/\s+/g, ' ')}…</span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { startEdit(h.systemPrompt); setShowHistory(false); }}
                >
                  Restore to editor
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────── Tile B · AI user notes ─────────────────────────

interface FoundUser { id: string; email: string; username: string; }

const UserNotesTile: React.FC<{ onError: (m: string) => void; onSuccess: (m: string) => void }> = ({
  onError,
  onSuccess,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<FoundUser | null>(null);
  const [notes, setNotes] = useState('');
  const [notesMeta, setNotesMeta] = useState<{ updatedAt: string | null; updatedBy: string | null }>({
    updatedAt: null,
    updatedBy: null,
  });
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [saving, setSaving] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const r = await apiService.searchAdminUsers(query.trim(), 20);
      setResults(r.users);
      if (r.users.length === 0) onError('No users matched that search.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'User search failed');
    } finally {
      setSearching(false);
    }
  };

  const pickUser = async (u: FoundUser) => {
    setSelected(u);
    setResults([]);
    setLoadingNotes(true);
    try {
      const n = await apiService.getAIUserNotes(u.id);
      setNotes(n.notes ?? '');
      setNotesMeta({ updatedAt: n.updatedAt, updatedBy: n.updatedBy });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load AI notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const n = await apiService.updateAIUserNotes(selected.id, notes);
      setNotesMeta({ updatedAt: n.updatedAt, updatedBy: n.updatedBy });
      onSuccess('AI notes saved.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to save AI notes');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setNotes('');
    setNotesMeta({ updatedAt: null, updatedBy: null });
    setResults([]);
    setQuery('');
  };

  return (
    <div className="card notes-card">
      <div className="card-body">
        <h2 className="section-h">AI notes</h2>
        <div className="notes-sub">
          The short keyword memory the AI keeps about a user (e.g. <em>name marie, loves bitcoin
          predictions, grandad was in the army</em>). These are dedicated AI notes — separate from any
          other admin notes. Never store card/bank details, documents or health data.
        </div>

        {!selected ? (
          <>
            <div className="notes-search">
              <input
                type="text"
                placeholder="Search by email or username…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
              />
              <button className="btn btn-primary" onClick={search} disabled={searching || !query.trim()}>
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>
            {results.length > 0 && (
              <div className="notes-results">
                {results.map((u) => (
                  <button key={u.id} className="notes-result" onClick={() => pickUser(u)}>
                    <span className="notes-result-name">{u.username || '(no username)'}</span>
                    <span className="notes-result-email">{u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="notes-editor-wrap">
            <div className="notes-user-row">
              <div>
                <div className="notes-user-name">{selected.username || '(no username)'}</div>
                <div className="notes-user-email">{selected.email}</div>
              </div>
              <button className="linkish" onClick={reset}>← search another user</button>
            </div>
            {loadingNotes ? (
              <div className="loading">Loading AI notes…</div>
            ) : (
              <>
                <textarea
                  className="notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="No notes yet. The AI will start building these as the user chats."
                  spellCheck={false}
                />
                <div className="notes-meta">
                  {notesMeta.updatedAt
                    ? `Last updated ${new Date(notesMeta.updatedAt).toLocaleString()} by ${notesMeta.updatedBy ?? '—'}`
                    : 'No notes saved yet.'}
                </div>
                <div className="soul-actions">
                  <button className="btn btn-primary" onClick={saveNotes} disabled={saving}>
                    {saving ? 'Saving…' : 'Save notes'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VersusAIPage;
